import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, FileText, Shield, ScrollText, ChevronRight } from 'lucide-react';

const LegalScreen = ({ onBack }) => {
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    { id: 'impressum', title: 'Impressum', icon: FileText },
    { id: 'datenschutz', title: 'Datenschutzerklärung', icon: Shield },
    { id: 'agb', title: 'AGB', icon: ScrollText },
  ];

  if (activeSection) {
    return (
      <div className="min-h-screen bg-background pb-24">
        <div className="px-4 pt-6 pb-4">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => setActiveSection(null)} aria-label="Zurück" data-testid="legal-close-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h2 className="text-xl font-display font-bold text-foreground">
              {sections.find(s => s.id === activeSection)?.title}
            </h2>
          </div>

          <Card className="border-border">
            <CardContent className="p-4">
              {activeSection === 'impressum' && <ImpressumContent />}
              {activeSection === 'datenschutz' && <DatenschutzContent />}
              {activeSection === 'agb' && <AGBContent />}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={onBack} aria-label="Zurück" data-testid="legal-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-display font-bold text-foreground">Rechtliches</h2>
            <p className="text-muted-foreground text-sm">Impressum, Datenschutz & AGB</p>
          </div>
        </div>

        <div className="space-y-3">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <Card
                key={section.id}
                className="border-border cursor-pointer hover:border-primary/50 transition-all active:scale-[0.98] animate-slide-up"
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => setActiveSection(section.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{section.title}</h4>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const ImpressumContent = () => (
  <div className="text-sm text-foreground space-y-4">
    <h3 className="text-lg font-semibold text-primary">Impressum</h3>
    
    <p><strong>Angaben gemäß § 5 TMG</strong></p>
    
    <p>
      S&T Brain-Trainer IM GbR<br/>
      Beerser Weg 3<br/>
      51588 Nümbrecht<br/>
      Deutschland
    </p>
    
    <p>
      <strong>Vertreten durch:</strong><br/>
      Serkan Öz<br/>
      Tommy Dethier
    </p>
    
    <p>
      <strong>Kontakt:</strong><br/>
      Telefon: +49 172 7442406 (Serkan Öz)<br/>
      Telefon: +49 157 86259215 (Tommy Dethier)<br/>
      E-Mail: <a href="mailto:info@stbrain-trainerim.de" className="text-primary hover:underline">info@stbrain-trainerim.de</a>
    </p>
    
    <p>
      <strong>Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:</strong><br/>
      Serkan Öz und Tommy Dethier<br/>
      Beerser Weg 3<br/>
      51588 Nümbrecht
    </p>
    
    <p>
      <strong>Haftung für Inhalte</strong><br/>
      Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte nach den allgemeinen Gesetzen verantwortlich.
    </p>
    
    <p>
      <strong>Haftung für Links</strong><br/>
      Unsere App enthält Links zu externen Websites Dritter. Für deren Inhalte übernehmen wir keine Haftung.
    </p>
  </div>
);

const DatenschutzContent = () => (
  <div className="text-sm text-foreground space-y-4">
    <h3 className="text-lg font-semibold text-primary">Datenschutzerklärung</h3>
    
    <p>
      Der Schutz Ihrer persönlichen Daten ist uns ein wichtiges Anliegen.
      Wir verarbeiten Ihre Daten ausschließlich im Rahmen der gesetzlichen Bestimmungen der DSGVO.
    </p>
    
    <h4 className="font-semibold text-foreground">1. Verantwortliche Stelle</h4>
    <p>
      S&T Brain-Trainer IM GbR<br/>
      Beerser Weg 3<br/>
      51588 Nümbrecht<br/>
      E-Mail: <a href="mailto:info@stbrain-trainerim.de" className="text-primary hover:underline">info@stbrain-trainerim.de</a>
    </p>
    
    <h4 className="font-semibold text-foreground">2. Datenverarbeitung in der App</h4>
    <p>
      Unsere App wird als Web-App innerhalb einer App-Umgebung (WebView) betrieben.
    </p>
    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
      <li>Nutzungsdaten (z. B. Lernfortschritt)</li>
      <li>Technische Daten (z. B. Gerätetyp, Browser)</li>
      <li>Lokal gespeicherte Daten (Local Storage)</li>
    </ul>
    
    <h4 className="font-semibold text-foreground">3. Speicherung von Lernständen</h4>
    <p className="text-muted-foreground">
      Lernstände und Fortschritte werden lokal auf dem Endgerät oder serverseitig gespeichert,
      sofern entsprechende Funktionen genutzt werden.
    </p>
    
    <h4 className="font-semibold text-foreground">4. Hosting</h4>
    <p className="text-muted-foreground">
      Die App wird über einen externen Hosting-Dienst (z. B. Emergent) betrieben.
      Dabei können technische Zugriffsdaten verarbeitet werden.
    </p>
    
    <h4 className="font-semibold text-foreground">5. Zahlungsabwicklung</h4>
    <p className="text-muted-foreground">
      Bei kostenpflichtigen Abonnements erfolgt die Zahlungsabwicklung über externe Zahlungsanbieter
      (z. B. PayPal). Es gelten deren Datenschutzbestimmungen.
    </p>
    
    <h4 className="font-semibold text-foreground">6. Rechte der Nutzer</h4>
    <ul className="list-disc list-inside space-y-1 text-muted-foreground">
      <li>Auskunft</li>
      <li>Berichtigung</li>
      <li>Löschung</li>
      <li>Einschränkung der Verarbeitung</li>
      <li>Widerspruch</li>
    </ul>
    <p className="text-muted-foreground">
      Anfragen richten Sie bitte an: <a href="mailto:info@stbrain-trainerim.de" className="text-primary hover:underline">info@stbrain-trainerim.de</a>
    </p>
    
    <h4 className="font-semibold text-foreground">7. Änderungen</h4>
    <p className="text-muted-foreground">
      Wir behalten uns vor, diese Datenschutzerklärung bei Bedarf anzupassen.
    </p>
  </div>
);

const AGBContent = () => (
  <div className="text-sm text-foreground space-y-4">
    <h3 className="text-lg font-semibold text-primary">Allgemeine Geschäftsbedingungen (AGB)</h3>
    
    <h4 className="font-semibold text-foreground">1. Geltungsbereich</h4>
    <p className="text-muted-foreground">
      Diese AGB gelten für die Nutzung der Lern-App S&T Brain-Trainer IM
      sowie aller darüber angebotenen Inhalte und Dienstleistungen.
    </p>
    
    <h4 className="font-semibold text-foreground">2. Leistungsbeschreibung</h4>
    <p className="text-muted-foreground">
      Die App dient der Lern- und Prüfungsvorbereitung für Industriemeister-Qualifikationen.
      Ein bestimmter Prüfungserfolg wird nicht garantiert.
    </p>
    
    <h4 className="font-semibold text-foreground">3. Nutzung der Inhalte</h4>
    <p className="text-muted-foreground">
      Die Inhalte sind ausschließlich für den persönlichen Lerngebrauch bestimmt.
      Eine Weitergabe oder kommerzielle Nutzung ist untersagt.
    </p>
    
    <h4 className="font-semibold text-foreground">4. Abonnements und Zahlungen</h4>
    <p className="text-muted-foreground">
      Sofern kostenpflichtige Abonnements angeboten werden, erfolgt die Abrechnung über externe Zahlungsanbieter.
      Kündigungen müssen über den jeweiligen Zahlungsanbieter erfolgen.
    </p>
    
    <h4 className="font-semibold text-foreground">5. Haftung</h4>
    <p className="text-muted-foreground">
      Die Nutzung der App erfolgt auf eigene Verantwortung.
      Für technische Ausfälle oder Datenverluste übernehmen wir keine Haftung,
      soweit gesetzlich zulässig.
    </p>
    
    <h4 className="font-semibold text-foreground">6. Verfügbarkeit</h4>
    <p className="text-muted-foreground">
      Wir bemühen uns um eine hohe Verfügbarkeit, können jedoch keine dauerhafte Erreichbarkeit garantieren.
    </p>
    
    <h4 className="font-semibold text-foreground">7. Schlussbestimmungen</h4>
    <p className="text-muted-foreground">
      Es gilt das Recht der Bundesrepublik Deutschland.
      Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen unberührt.
    </p>
  </div>
);

export default LegalScreen;